import React from 'react';
import { Box, Typography } from '@mui/material';
import { DEFAULT_INVOICE_SELLER } from '../constants/invoiceSellerBank';

export const INVOICE_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/teniso-uzsiemimai-invoice-logo.png`;

export function FieldRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <Typography variant="body2" sx={{ mb: 0.5, lineHeight: 1.5 }}>
      <Box component="span" sx={{ fontWeight: 600 }}>{label}</Box>
      {` ${value}`}
    </Typography>
  );
}

/**
 * Logotipas kairėje, antraštė ir serija centre (spausdinimui / PDF).
 */
export function InvoiceHeaderWithLogo({ title, serialNo }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        mb: 2,
        '@media print': { breakInside: 'avoid' },
      }}
    >
      <Box
        component="img"
        src={INVOICE_LOGO_SRC}
        alt="Teniso Užsiėmimai.lt"
        sx={{
          width: 100,
          maxHeight: 100,
          height: 'auto',
          objectFit: 'contain',
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
        <Typography variant="h6" align="center" sx={{ mb: 0.5, fontWeight: 400 }}>
          {title}
        </Typography>
        {serialNo != null && serialNo !== '' && (
          <Typography variant="body1" align="center" sx={{ mb: 0 }}>
            Serijos Nr.-{serialNo}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * Pardavėjo blokas su Paysera rekvizitais.
 */
export function PardavejasWithBankBlock({ seller = DEFAULT_INVOICE_SELLER, heading = 'Pardavėjas' }) {
  const s = { ...DEFAULT_INVOICE_SELLER, ...seller };
  return (
    <Box sx={{ flex: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{heading}</Typography>
      <FieldRow label="Pavadinimas:" value={s.name} />
      <FieldRow label="Adresas:" value={s.address} />
      <FieldRow label="Įmonės kodas:" value={s.companyCode} />
      <FieldRow label="IBAN (EUR, SEPA):" value={s.accountIban} />
      <FieldRow label="Bankas:" value={s.bank} />
      <FieldRow label="Banko kodas:" value={s.bankCode} />
      <FieldRow label="Swift:" value={s.swift} />
      <FieldRow label="Banko adresas:" value={s.bankAddress} />
      {s.sepaNote && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, lineHeight: 1.4 }}>
          {s.sepaNote}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Shown only on screen — hidden when printing so it does not appear on the invoice.
 * Explains how to remove browser-added URL/date from “Save as PDF”.
 */
export function InvoicePrintBrowserHint() {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      component="p"
      sx={{
        mt: 2,
        mb: 0,
        maxWidth: '72ch',
        lineHeight: 1.5,
        '@media print': { display: 'none' },
      }}
    >
      Jei PDF apačioje matote puslapio adresą ar datą — tai naršyklės antraštė, ne sąskaitos dalis.
      Spausdinimo lange išjunkite „Antraštės ir poraštės“ (Chrome / Edge: „Headers and footers“; Safari: atitinkama parinktis),
      tada išsaugokite kaip PDF.
    </Typography>
  );
}

/** Pirkėjo blokas (A/s, bankas – jei užpildyti teste). */
export function PirkėjasBlock({ buyer, heading = 'Pirkėjas' }) {
  return (
    <Box sx={{ flex: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{heading}</Typography>
      <FieldRow label="Pavadinimas:" value={buyer.name} />
      <FieldRow label="Adresas:" value={buyer.address} />
      <FieldRow label="Įmonės kodas:" value={buyer.companyCode} />
      <FieldRow label="A/s nr:" value={buyer.accountNo} />
      <FieldRow label="Bankas:" value={buyer.bank} />
      <FieldRow label="Banko kodas:" value={buyer.bankCode} />
      <FieldRow label="Swift:" value={buyer.swift} />
    </Box>
  );
}
